import type { ReactNode } from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Except } from "type-fest";

import { findInstrumentDisplayName } from "@thalamos/haven-common";

import { AssessmentKindChip } from "../AssessmentKindChip/AssessmentKindChip.js";
import { GuestAssessor, GuestChip } from "../GuestAssessor/GuestAssessor.js";
import type { FinishedAssessmentRow } from "./finished_assessment_row.js";

const mediumDate = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

/**
 * What each column of a finished assessment's row shows, so every list of
 * finished assessments draws a row the same way, and the assessor's name for
 * the row's labels.
 */
export const useFinishedAssessmentCells = ({
  assessment,
  assessorName,
  approverName,
  guestAsk,
  guestsSharedWith,
}: Except<FinishedAssessmentRow, "actions">) => {
  const { t } = useTranslation();
  const name = assessorName ?? t("personPage.assessments.unknownAssessor");

  return {
    /** The assessor as the row's labels name them. */
    assessorName: name,
    kind: assessment.definitive ? (
      <AssessmentKindChip definitive />
    ) : (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        <AssessmentKindChip definitive={false} />
        {guestAsk === undefined ? null : <GuestChip />}
      </Stack>
    ),
    instrument: findInstrumentDisplayName(assessment.instrument.name) ?? null,
    assessor:
      guestAsk === undefined ? (
        name
      ) : (
        <GuestAssessor name={name} guestAsk={guestAsk} withGuest={false} />
      ),
    approvedBy:
      assessment.status === "approved"
        ? (approverName ?? t("personPage.assessments.history.unknownApprover"))
        : t("personPage.assessments.history.noApprovalNeeded"),
    finished: (
      <>
        {t(`personPage.assessments.history.finished.${assessment.status}`, {
          date: mediumDate.format(assessment.finishedAt),
        })}
        {guestsSharedWith === 0 ? null : (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {t("personPage.assessments.history.sharedWith", {
              count: guestsSharedWith,
            })}
          </Typography>
        )}
      </>
    ),
  } satisfies Record<string, ReactNode>;
};
